import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  MocChange,
  MocImpactAssessment,
  MocRiskSnapshot,
  MocApprovalWorkflow,
  MocImplementationPlan,
  MocPostImplementationReview,
  MocClosureRecord,
  MocStatusHistory,
  ImpactDomain
} from './types';
import { mocService } from './mocService';

interface Props {
  changeId: string;
  organizationId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const MocDetailModal: React.FC<Props> = ({ changeId, organizationId, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'impact' | 'risk' | 'approval' | 'implementation' | 'pir' | 'closure' | 'history'>('overview');
  const [change, setChange] = useState<MocChange | null>(null);
  const [impacts, setImpacts] = useState<MocImpactAssessment[]>([]);
  const [risks, setRisks] = useState<MocRiskSnapshot[]>([]);
  const [approval, setApproval] = useState<MocApprovalWorkflow | null>(null);
  const [plan, setPlan] = useState<MocImplementationPlan | null>(null);
  const [pir, setPir] = useState<MocPostImplementationReview | null>(null);
  const [closure, setClosure] = useState<MocClosureRecord | null>(null);
  const [history, setHistory] = useState<MocStatusHistory[]>([]);
  const [loading, setLoading] = useState(true);

  // Forms states
  const [transitionStatus, setTransitionStatus] = useState<string>('SCREENING');
  const [transitionReason, setTransitionReason] = useState('');

  const [newImpact, setNewImpact] = useState({
    domain: 'SAFETY' as ImpactDomain,
    assessment_summary: '',
    impact_level: 'MEDIUM',
    required_mitigation_summary: ''
  });

  const [newRisk, setNewRisk] = useState({
    risk_title: '',
    initial_severity: 4,
    initial_likelihood: 3,
    initial_risk_index: '4C',
    residual_severity: 2,
    residual_likelihood: 1,
    residual_risk_index: '2A',
    tolerability: 'ACCEPTABLE',
    mitigation_controls: ''
  });

  const [pirForm, setPirForm] = useState({
    lead_reviewer_name: 'Lead Auditor',
    is_conformance_verified: true,
    is_effective: true,
    effectiveness_score: 90,
    conformance_notes: '',
    recommendations: ''
  });

  const [closureForm, setClosureForm] = useState({
    closure_summary: '',
    lessons_learned: ''
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [c, imp, r, p, pi, cl, h] = await Promise.all([
        mocService.getChangeById(changeId, organizationId),
        mocService.getImpacts(changeId, organizationId).catch(() => []),
        mocService.getRisks(changeId, organizationId).catch(() => []),
        mocService.getImplementationPlan(changeId, organizationId).catch(() => null),
        mocService.getPIR(changeId, organizationId).catch(() => null),
        mocService.getClosure(changeId, organizationId).catch(() => null),
        mocService.getStatusHistory(changeId, organizationId).catch(() => [])
      ]);
      setChange(c);
      setImpacts(imp);
      setRisks(r);
      setPlan(p);
      setPir(pi);
      setClosure(cl);
      setHistory(h);

      const app = await mocService.getApprovalWorkflow(changeId, organizationId).catch(() => null);
      setApproval(app);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [changeId]);

  const handleTransition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transitionReason) return alert('Please provide a reason for status transition.');
    try {
      await mocService.transitionStatus(
        changeId,
        transitionStatus,
        transitionReason,
        'USR-CURRENT-USER',
        'Authorized Coordinator',
        organizationId
      );
      setTransitionReason('');
      loadData();
      onRefresh();
    } catch (err) {
      alert('Status transition failed: ' + (err as Error).message);
    }
  };

  const handleAddImpact = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await mocService.addImpact(
        changeId,
        {
          ...newImpact,
          is_affected: true,
          assessed_by_id: 'USR-IMPACT-01',
          assessed_by_name: 'Lead Impact Assessor'
        },
        organizationId
      );
      setNewImpact({ domain: 'SAFETY', assessment_summary: '', impact_level: 'MEDIUM', required_mitigation_summary: '' });
      loadData();
      onRefresh();
    } catch (err) {
      alert('Failed to save impact assessment: ' + (err as Error).message);
    }
  };

  const handleAddRisk = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await mocService.addRisk(
        changeId,
        {
          ...newRisk,
          tolerability: newRisk.tolerability as any,
          assessed_by: 'Safety Assessment Team'
        },
        organizationId
      );
      setNewRisk({
        risk_title: '',
        initial_severity: 4,
        initial_likelihood: 3,
        initial_risk_index: '4C',
        residual_severity: 2,
        residual_likelihood: 1,
        residual_risk_index: '2A',
        tolerability: 'ACCEPTABLE',
        mitigation_controls: ''
      });
      loadData();
      onRefresh();
    } catch (err) {
      alert('Failed to save risk assessment: ' + (err as Error).message);
    }
  };

  const handleInitiateApproval = async () => {
    try {
      await mocService.initiateApproval(
        changeId,
        {
          initiated_by: change?.initiator_name || 'Initiator',
          required_signoffs_count: 2,
          steps: [
            { step_order: 1, approver_role: 'SAFETY_MANAGER', approver_user_id: 'USR-SAFE-MGR', approver_name: 'Kemal Yilmaz' },
            { step_order: 2, approver_role: 'ACCOUNTABLE_MANAGER', approver_user_id: 'USR-AM-01', approver_name: 'Cemil Demir' }
          ]
        },
        organizationId
      );
      loadData();
      onRefresh();
    } catch (err) {
      alert('Failed to initiate approval workflow: ' + (err as Error).message);
    }
  };

  const handleApproveStep = async (stepId: string) => {
    try {
      await mocService.recordApprovalDecision(
        changeId,
        {
          step_id: stepId,
          approver_user_id: 'USR-SAFE-MGR',
          approver_name: 'Kemal Yilmaz (Safety Manager)',
          decision: 'APPROVED',
          decision_reason: 'Risk controls, operational barriers, and SOP updates reviewed and approved.'
        },
        organizationId
      );
      loadData();
      onRefresh();
    } catch (err) {
      alert('Approval action failed: ' + (err as Error).message);
    }
  };

  const handleRecordPIR = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await mocService.recordPIR(
        changeId,
        {
          ...pirForm,
          lead_reviewer_id: 'USR-PIR-LEAD',
          residual_risks_controlled: true
        },
        organizationId
      );
      loadData();
      onRefresh();
    } catch (err) {
      alert('Failed to record PIR: ' + (err as Error).message);
    }
  };

  const handleCloseMOC = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await mocService.closeChange(
        changeId,
        {
          closed_by_id: 'USR-AM-01',
          closed_by_name: 'Cemil Demir (Accountable Manager)',
          closure_summary: closureForm.closure_summary,
          lessons_learned: closureForm.lessons_learned
        },
        organizationId
      );
      loadData();
      onRefresh();
    } catch (err) {
      alert('Failed to close change record: ' + (err as Error).message);
    }
  };

  if (loading || !change) {
    return (
      <div className="modal-backdrop">
        <div className="modal-card max-w-md p-8 text-center text-gray-600">
          Loading Management of Change details...
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-5xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                {change.moc_number}
              </span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-600 uppercase font-semibold">{change.change_type}</span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">{change.status}</span>
            </div>
            <h3>{change.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 px-6 gap-2 bg-gray-50 overflow-x-auto text-xs font-medium">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`py-3 px-3 border-b-2 transition ${
              activeTab === 'overview' ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('impact')}
            className={`py-3 px-3 border-b-2 transition ${
              activeTab === 'impact' ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Impact Assessments ({impacts.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('risk')}
            className={`py-3 px-3 border-b-2 transition ${
              activeTab === 'risk' ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Risk Assessment ({risks.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('approval')}
            className={`py-3 px-3 border-b-2 transition ${
              activeTab === 'approval' ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Approval Chain & SoD
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('implementation')}
            className={`py-3 px-3 border-b-2 transition ${
              activeTab === 'implementation' ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Implementation Plan
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('pir')}
            className={`py-3 px-3 border-b-2 transition ${
              activeTab === 'pir' ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Post-Implementation Review (PIR)
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('closure')}
            className={`py-3 px-3 border-b-2 transition ${
              activeTab === 'closure' ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Closure Assurance
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`py-3 px-3 border-b-2 transition ${
              activeTab === 'history' ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Audit Trail
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs">
                <div>
                  <span className="text-gray-500 block">Change Owner</span>
                  <strong className="text-gray-900 text-sm">{change.change_owner_name}</strong>
                </div>
                <div>
                  <span className="text-gray-500 block">Department</span>
                  <strong className="text-gray-900 text-sm">{change.target_department}</strong>
                </div>
                <div>
                  <span className="text-gray-500 block">Duration</span>
                  <strong className="text-gray-900 text-sm">{change.duration_type}</strong>
                </div>
                <div>
                  <span className="text-gray-500 block">Execution Mode</span>
                  <strong className="text-gray-900 text-sm">{change.execution_type}</strong>
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-2">
                <span className="text-xs text-gray-500 font-semibold block uppercase">Justification & Scope</span>
                <p className="text-sm text-gray-800 leading-relaxed">{change.description}</p>
              </div>

              {/* Status Transition Form */}
              {change.status !== 'CLOSED' && (
                <form onSubmit={handleTransition} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Lifecycle State Transition</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="form-field">
                      <label>Target Status</label>
                      <select
                        value={transitionStatus}
                        onChange={(e) => setTransitionStatus(e.target.value)}
                      >
                        <option value="SCREENING">SCREENING</option>
                        <option value="IMPACT_ASSESSMENT">IMPACT ASSESSMENT</option>
                        <option value="RISK_ASSESSMENT">RISK ASSESSMENT</option>
                        <option value="APPROVAL">APPROVAL</option>
                        <option value="IMPLEMENTATION">IMPLEMENTATION</option>
                        <option value="MONITORING">MONITORING</option>
                        <option value="PIR">PIR (REVIEW)</option>
                      </select>
                    </div>
                    <div className="form-field md:col-span-2">
                      <label>Transition Reason *</label>
                      <input
                        type="text"
                        required
                        placeholder="Operational justification for advancing status..."
                        value={transitionReason}
                        onChange={(e) => setTransitionReason(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-2">
                    <button type="submit" className="btn-primary">
                      Update Lifecycle Status
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* TAB 2: IMPACT */}
          {activeTab === 'impact' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold text-gray-900">Cross-Department Impact Assessments</h3>
              </div>

              <div className="space-y-3">
                {impacts.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-xs">No impact assessment logged yet.</div>
                ) : (
                  impacts.map((imp) => (
                    <div key={imp.id} className="p-4 bg-white rounded-xl border border-gray-200 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-xs text-blue-700">{imp.domain}</span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                          {imp.impact_level} IMPACT
                        </span>
                      </div>
                      <p className="text-xs text-gray-700">{imp.assessment_summary}</p>
                      {imp.required_mitigation_summary && (
                        <div className="text-[11px] text-emerald-800 bg-emerald-50 p-2.5 rounded border border-emerald-200">
                          <strong>Required Mitigation:</strong> {imp.required_mitigation_summary}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Add Impact Form */}
              <form onSubmit={handleAddImpact} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                <h4 className="text-xs font-semibold text-gray-900">Add New Impact Assessment</h4>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Domain</label>
                    <select
                      value={newImpact.domain}
                      onChange={(e) => setNewImpact({ ...newImpact, domain: e.target.value as any })}
                    >
                      <option value="SAFETY">Safety</option>
                      <option value="FLIGHT_OPERATIONS">Flight Operations</option>
                      <option value="AIRWORTHINESS_MAINTENANCE">Airworthiness & Maintenance</option>
                      <option value="GROUND_OPS">Ground Operations</option>
                      <option value="SECURITY">Security</option>
                      <option value="REGULATORY_COMPLIANCE">Regulatory Compliance</option>
                      <option value="IT_SYSTEMS">IT & Systems</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Impact Level</label>
                    <select
                      value={newImpact.impact_level}
                      onChange={(e) => setNewImpact({ ...newImpact, impact_level: e.target.value })}
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="CRITICAL">Critical</option>
                    </select>
                  </div>
                </div>
                <div className="form-field">
                  <label>Assessment Summary *</label>
                  <textarea
                    placeholder="Describe operational impact and implications on this domain..."
                    rows={2}
                    required
                    value={newImpact.assessment_summary}
                    onChange={(e) => setNewImpact({ ...newImpact, assessment_summary: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Required Mitigation Measures</label>
                  <input
                    type="text"
                    placeholder="Specific risk controls or procedural adjustments..."
                    value={newImpact.required_mitigation_summary}
                    onChange={(e) => setNewImpact({ ...newImpact, required_mitigation_summary: e.target.value })}
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <button type="submit" className="btn-primary">
                    Save Impact Assessment
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* TAB 3: RISK */}
          {activeTab === 'risk' && (
            <div className="space-y-6">
              <h3 className="text-sm font-semibold text-gray-900">Hazard Identification & Risk Snapshots</h3>
              <div className="space-y-3">
                {risks.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-xs">No risk assessment recorded yet.</div>
                ) : (
                  risks.map((r) => (
                    <div key={r.id} className="p-4 bg-white rounded-xl border border-gray-200 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-xs text-gray-900">{r.risk_title}</span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                          {r.tolerability}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-xs bg-gray-50 p-2.5 rounded border border-gray-200">
                        <div>
                          <span className="text-gray-500 block">Initial Risk Index:</span>
                          <strong className="text-amber-700">{r.initial_risk_index}</strong>
                        </div>
                        <div>
                          <span className="text-gray-500 block">Residual Risk Index:</span>
                          <strong className="text-emerald-700">{r.residual_risk_index}</strong>
                        </div>
                      </div>
                      {r.mitigation_controls && (
                        <p className="text-xs text-gray-600">
                          <strong>Barriers & Controls:</strong> {r.mitigation_controls}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Add Risk Form */}
              <form onSubmit={handleAddRisk} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                <h4 className="text-xs font-semibold text-gray-900">Add Risk Assessment</h4>
                <div className="form-field">
                  <label>Hazard / Risk Title *</label>
                  <input
                    type="text"
                    placeholder="e.g. Flight crew confusion during dual-system transitional phase"
                    required
                    value={newRisk.risk_title}
                    onChange={(e) => setNewRisk({ ...newRisk, risk_title: e.target.value })}
                  />
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Initial Risk Index *</label>
                    <input
                      type="text"
                      placeholder="e.g. 4C"
                      required
                      value={newRisk.initial_risk_index}
                      onChange={(e) => setNewRisk({ ...newRisk, initial_risk_index: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>Residual Risk Index *</label>
                    <input
                      type="text"
                      placeholder="e.g. 2A"
                      required
                      value={newRisk.residual_risk_index}
                      onChange={(e) => setNewRisk({ ...newRisk, residual_risk_index: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-field">
                  <label>Mitigation Controls & Barriers</label>
                  <input
                    type="text"
                    placeholder="ALARP controls, standard operating procedures, flight crew briefings..."
                    value={newRisk.mitigation_controls}
                    onChange={(e) => setNewRisk({ ...newRisk, mitigation_controls: e.target.value })}
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <button type="submit" className="btn-primary">
                    Save Risk Assessment
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* TAB 4: APPROVAL */}
          {activeTab === 'approval' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold text-gray-900">Approval Workflow (Segregation of Duties - SoD)</h3>
                {!approval && (
                  <button
                    type="button"
                    onClick={handleInitiateApproval}
                    className="btn-primary"
                  >
                    + Initiate Approval Workflow
                  </button>
                )}
              </div>

              {approval ? (
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs flex justify-between">
                    <span>Workflow Status: <strong className="text-purple-700">{approval.workflow_status}</strong></span>
                    <span>Initiated By: <strong className="text-gray-900">{approval.initiated_by}</strong></span>
                  </div>

                  <div className="space-y-2">
                    {approval.steps?.map((step) => (
                      <div key={step.id} className="p-4 bg-white rounded-xl border border-gray-200 flex justify-between items-center text-xs">
                        <div>
                          <span className="font-bold text-gray-900 block">Step {step.step_order}: {step.approver_role}</span>
                          <span className="text-gray-500 block mt-0.5">
                            {step.approver_name || 'Pending assignment'} • <span className="font-semibold text-blue-700">{step.decision}</span>
                          </span>
                          {step.decision_reason && (
                            <span className="text-[11px] text-gray-600 italic block mt-1">"{step.decision_reason}"</span>
                          )}
                        </div>
                        {step.decision === 'PENDING' && (
                          <button
                            type="button"
                            onClick={() => handleApproveStep(step.id)}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm transition"
                          >
                            ✓ Approve Step
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 text-xs">No approval workflow initiated for this change yet.</div>
              )}
            </div>
          )}

          {/* TAB 5: IMPLEMENTATION */}
          {activeTab === 'implementation' && (
            <div className="space-y-6">
              <h3 className="text-sm font-semibold text-gray-900">Implementation & Rollback Safeguards</h3>
              {plan ? (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-xs space-y-2">
                    <span className="font-bold text-gray-900 block text-sm">{plan.title}</span>
                    <div className="flex gap-4 text-gray-500">
                      <span>Start: {plan.target_start_date}</span>
                      <span>Target Completion: {plan.target_end_date}</span>
                    </div>
                    {plan.has_rollback_plan && (
                      <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs mt-2">
                        <strong>Rollback Procedure:</strong> {plan.rollback_procedure}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-gray-500">Implementation Steps</h4>
                    {plan.steps?.map((step) => (
                      <div key={step.id} className="p-3 bg-white rounded-xl border border-gray-200 flex justify-between items-center text-xs">
                        <div>
                          <span className="font-bold text-gray-900 block">Step {step.step_sequence}: {step.title}</span>
                          <span className="text-gray-500 block mt-0.5">
                            {step.assigned_department} • {step.assignee_name} • Due: {step.due_date}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          step.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {step.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 text-xs">Implementation plan not yet recorded.</div>
              )}
            </div>
          )}

          {/* TAB 6: PIR */}
          {activeTab === 'pir' && (
            <div className="space-y-6">
              <h3 className="text-sm font-semibold text-gray-900">Post-Implementation Review (PIR)</h3>
              {pir ? (
                <div className="p-4 bg-white rounded-xl border border-gray-200 text-xs space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-gray-900 text-sm">PIR No: {pir.review_number}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                      EFFECTIVENESS SCORE: {pir.effectiveness_score}%
                    </span>
                  </div>
                  <p className="text-gray-700">Lead Reviewer: {pir.lead_reviewer_name} ({pir.review_date})</p>
                  {pir.conformance_notes && <p className="text-gray-600"><strong>Conformance Notes:</strong> {pir.conformance_notes}</p>}
                  {pir.recommendations && <p className="text-gray-600"><strong>Recommendations:</strong> {pir.recommendations}</p>}
                </div>
              ) : (
                <form onSubmit={handleRecordPIR} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-900">Complete PIR Evaluation</h4>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Lead Reviewer *</label>
                      <input
                        type="text"
                        placeholder="Lead Auditor / Reviewer..."
                        required
                        value={pirForm.lead_reviewer_name}
                        onChange={(e) => setPirForm({ ...pirForm, lead_reviewer_name: e.target.value })}
                      />
                    </div>
                    <div className="form-field">
                      <label>Effectiveness Score (0-100) *</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="e.g. 95"
                        required
                        value={pirForm.effectiveness_score}
                        onChange={(e) => setPirForm({ ...pirForm, effectiveness_score: Number(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Conformance & Verification Notes</label>
                    <textarea
                      placeholder="Evaluation notes, audit sample verification..."
                      rows={2}
                      value={pirForm.conformance_notes}
                      onChange={(e) => setPirForm({ ...pirForm, conformance_notes: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>Recommendations & Follow-up Items</label>
                    <textarea
                      placeholder="Recommendations for continued monitoring..."
                      rows={2}
                      value={pirForm.recommendations}
                      onChange={(e) => setPirForm({ ...pirForm, recommendations: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <button type="submit" className="btn-primary">
                      Save PIR Record
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* TAB 7: CLOSURE */}
          {activeTab === 'closure' && (
            <div className="space-y-6">
              <h3 className="text-sm font-semibold text-gray-900">Closure Gates & Formal Assurance</h3>
              {closure ? (
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-xs space-y-2">
                  <span className="font-bold text-emerald-900 block text-sm">✓ CHANGE FORMALLY CLOSED (IMMUTABLE)</span>
                  <p className="text-emerald-800">Closed By: {closure.closed_by_name} ({closure.closed_at})</p>
                  <p className="text-emerald-800"><strong>Summary:</strong> {closure.closure_summary}</p>
                  {closure.lessons_learned && (
                    <p className="text-emerald-700"><strong>Lessons Learned:</strong> {closure.lessons_learned}</p>
                  )}
                </div>
              ) : (
                <form onSubmit={handleCloseMOC} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>• PIR review verification must be completed prior to closure.</p>
                    <p>• Closed MOC records are immutable and retained as permanent audit trail evidence.</p>
                  </div>
                  <div className="form-field">
                    <label>Closure Summary & Management Evaluation *</label>
                    <textarea
                      placeholder="Summarize change completion, outcome verification, and operational signoff..."
                      rows={3}
                      required
                      value={closureForm.closure_summary}
                      onChange={(e) => setClosureForm({ ...closureForm, closure_summary: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>Lessons Learned</label>
                    <textarea
                      placeholder="Key takeaways, procedural adjustments identified, or improvements..."
                      rows={2}
                      value={closureForm.lessons_learned}
                      onChange={(e) => setClosureForm({ ...closureForm, lessons_learned: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm transition">
                      Formally Close Change
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* TAB 8: HISTORY */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Audit Trail & Status Transition History</h3>
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.id} className="p-3 bg-white rounded-xl border border-gray-200 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-blue-700">
                        {h.from_status} → {h.to_status}
                      </span>
                      <span className="text-[11px] text-gray-500 font-mono">{h.created_at}</span>
                    </div>
                    <p className="text-gray-700 mt-1">"{h.reason}"</p>
                    <span className="text-[10px] text-gray-400 block mt-1 font-mono">Action Performed By: {h.changed_by_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
